import { Box, Grid, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { requestWithUserAuth } from '../../../api/auth'
import { cancelVote, vote } from '../../../api/voting'
import { pallete } from '../../../theme'
import { Button, TranslatableTypography } from '../widgets'
import { VotingCountdown } from './Countdown'
import { VotedIcon, VoteIcon } from './icons'

const stylePrefix = 'CompanionVotingControls'

const style = {
  [`&.${stylePrefix}-root`]: {
    marginTop: '10px',
    marginBottom: '10px',
    textAlign: 'center',
    [`& .${stylePrefix}-controls-wrapper`]: {
      marginTop: '10px',
      minWidth: 0,
      overflow: 'hidden',
      [`& .${stylePrefix}-item`]: {
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer',
        minWidth: 0,
        overflow: 'hidden',
        '& p': {
          color: pallete.headerColor,
          fontWeight: 300
        },
        '&.selected p': {
          color: pallete.headerColor,
          fontWeight: 900
        },
        [`& .${stylePrefix}-icon`]: {
          height: '120px',
          width: '120px',
          display: 'inline-block',
          opacity: 0.2,
          '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }
        },
        [`& .${stylePrefix}-icon.${stylePrefix}-icon-small`]: {
          height: 'clamp(44px, 18vw, 80px)',
          width: 'clamp(44px, 18vw, 80px)'
        },
        [`&.selected .${stylePrefix}-icon`]: {
          opacity: 1
        },
        [`& .${stylePrefix}-button-wrapper`]: {
          position: 'absolute',
          bottom: '0px',
          width: '100%',
          '& button': {
            height: '36px'
          }
        },
        [`& .${stylePrefix}-button-wrapper.${stylePrefix}-button-wrapper-small`]: {
          paddingLeft: '4px',
          paddingRight: '4px',
          paddingTop: '6px',
          '& .MuiButton-root': {
            height: '26px',
            fontSize: '0.62rem !important',
            minWidth: 0,
            width: '100%',
            paddingLeft: '4px',
            paddingRight: '4px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            '&:active': { boxShadow: 'none' }
          }
        }
      }
    },
    [`& .${stylePrefix}-available-wrapper`]: {
      [`& .${stylePrefix}-item`]: {
        padding: '5px',
        '& p': {
          display: 'inline-block',
          paddingRight: '5px'
        },
        [`& .${stylePrefix}-label-wrapper`]: {
          textAlign: 'right'
        },
        [`& .${stylePrefix}-votes-wrapper`]: {
          textAlign: 'left'
        }
      },
      [`& .${stylePrefix}-icon-wrapper`]: {
        display: 'inline-block',
        verticalAlign: 'middle',
        '& img': {
          width: '25px',
          verticalAlign: 'middle'
        },
        '&.voted': {
          opacity: '0.25'
        }
      }
    }
  }
}

const CampaignAvailableVotes = (props) => {
  const { remaining, voted, titles } = props
  const votedControl = []
  for (let i = 0; i < voted; i++) {
    votedControl.push(
      <VoteIcon key={i} className={`${stylePrefix}-icon-wrapper voted`}/>
    )
  }
  const remainingVotesControl = []
  for (let i = 0; i < remaining; i++) {
    remainingVotesControl.push(
      <VotedIcon key={i} className={`${stylePrefix}-icon-wrapper`}/>
    )
  }

  return (
    <Grid item xs={12} className={`${stylePrefix}-item`}>
      <Grid container>
        <Grid item xs={8} className={`${stylePrefix}-label-wrapper`}>
          <TranslatableTypography data={titles}/>
          <TranslatableTypography text={`(remaining ${remaining}):`}/>
        </Grid>
        <Grid item xs={4} className={`${stylePrefix}-votes-wrapper`}>
          {remainingVotesControl}
          {votedControl}
        </Grid>
      </Grid>
    </Grid>
  )
}

const CampaignControl = (props) => {
  const { campaign, votes, number, remaining, config, auth, deadline, category: categoryProp, singleRow, votingNotStarted } = props
  const { titles, name } = campaign
  const voteCategory = categoryProp ?? 'fursuit'
  const voted = votes[number] ?? []
  const included = voted.includes(name)
  const selected = included ? ' selected' : ''
  const label = included ? 'Cancel Vote' : 'Vote'
  const iconClass = singleRow
    ? `${stylePrefix}-icon ${stylePrefix}-icon-small`
    : `${stylePrefix}-icon`
  const voteIcon = included ? <VotedIcon className={iconClass}/> : <VoteIcon className={iconClass}/>

  const onCancel = () => {
    requestWithUserAuth(config, auth, (auth) => {
      cancelVote(config, auth, voteCategory, name, number)
    })
  }

  const onVote = () => {
    requestWithUserAuth(config, auth, (auth) => {
      vote(config, auth, voteCategory, name, number, {
        value: 1
      })
    })
  }

  const buttonWrapperClass = singleRow
    ? `${stylePrefix}-button-wrapper ${stylePrefix}-button-wrapper-small`
    : `${stylePrefix}-button-wrapper`

  let render
  if (votingNotStarted || (deadline != null && deadline <= 0)) {
    render = (
      <Box className={buttonWrapperClass} />
    )
  } else if (remaining === 0 && !included) {
    render = (
      <Box className={buttonWrapperClass}>
        <TranslatableTypography text="No votes remaining"/>
      </Box>
    )
  } else {
    render = (
      <Box className={buttonWrapperClass}>
        <Button variant='contained' label={label} onClick={included ? onCancel : onVote} sx={singleRow ? { minWidth: 0, width: '100%', fontSize: '0.7rem !important', px: 0.5, height: '28px' } : {}} />
      </Box>
    )
  }

  return (
    <Grid item xs={singleRow ? true : 6} className={`${stylePrefix}-item${selected}`} sx={singleRow ? { overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column', px: 0.5 } : {}}>
      <Box sx={{ height: singleRow ? '2.6em' : undefined, minHeight: singleRow ? undefined : '2.8em', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden', pb: singleRow ? '2px' : 0 }}>
        <TranslatableTypography data={titles} sx={singleRow ? { fontSize: '0.7rem', lineHeight: 1.3, textAlign: 'center' } : {}}/>
      </Box>
      <Box sx={singleRow ? { display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' } : { display: 'contents' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {voteIcon}
          {included && singleRow && (
            <Typography sx={{ fontSize: '0.6rem', color: pallete.headerColor, fontWeight: 700, lineHeight: 1.2 }}>Voted</Typography>
          )}
        </Box>
        {render}
      </Box>
    </Grid>
  )
}

export const VotingControls = (props) => {
  const { config } = useSelector((state) => state.config)
  const auth = useSelector((state) => state.auth)
  const { number: fursuitNumber } = useSelector(state => state.fursuitVoting)
  const { campaigns, votes, deadline, entityId, category } = props
  const number = entityId !== undefined ? entityId : fursuitNumber
  const availableVotes = []
  const voteControls = []
  campaigns.forEach((campaign, i) => {
    const { campaign_max, titles } = campaign
    const voted = campaign.votes.reduce((accumulator, vote) => {
      const { value } = vote
      const acucmulated = accumulator + value
      return acucmulated < campaign_max ? acucmulated : campaign_max
    }, 0)
    const remaining = campaign_max - voted

    const availableVotesProps = {
      key: i,
      remaining,
      voted,
      titles
    }
    availableVotes.push(<CampaignAvailableVotes {...availableVotesProps} />)

    const voteControlsProps = {
      key: i,
      auth,
      config,
      remaining,
      votes,
      campaign,
      number,
      deadline,
      category,
      singleRow: props.singleRow,
      votingNotStarted: props.votingNotStarted
    }
    voteControls.push(<CampaignControl {...voteControlsProps} />)
  })
  return (
    <Box sx={style} className={`${stylePrefix}-root`}>
      {!props.hideAvailable && (
        <Grid container className={`${stylePrefix}-available-wrapper`}>
          {availableVotes}
        </Grid>
      )}
      <VotingCountdown deadlineMs={props.deadlineMs} votingStartMs={props.votingStartMs} votingNotStarted={props.votingNotStarted} />
      <Box sx={{ position: 'relative' }}>
        <Grid container className={`${stylePrefix}-controls-wrapper`} sx={props.singleRow ? { flexWrap: 'nowrap', minWidth: 0, overflow: 'hidden', alignItems: 'stretch' } : {}}>
          {voteControls}
        </Grid>
        {(props.votingNotStarted || (props.deadline != null && props.deadline <= 0)) && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'background.paper', opacity: 0.82, borderRadius: 1 }} />
            <Typography sx={{ position: 'relative', fontSize: '0.75rem', fontWeight: 700, opacity: 0.6, textAlign: 'center', px: 1 }}>
              {props.votingNotStarted ? 'Voting not started' : 'Voting has ended'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}
